import React from "react";
import AdminNavbar from "components/Navbars/AdminNavbar";
import Sidebar from "components/Sidebar/Sidebar";
import { useAuth } from "pages/Auth/AuthContext";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import routes from "Routes/channelRoutes";
import LoadingScreen from "./LoadingScreen";
import { ROLE_BASED_DEFAULT_ROUTE } from "constants/CommonConstants";

function ChannelPartner() {
  const [sidenavOpen, setSidenavOpen] = React.useState(window.innerWidth >= 1030);
  const location = useLocation();
  const mainContentRef = React.useRef(null);
  const { role, isAuthenticated, loading } = useAuth(); // get user role and auth state

  const normalizedRole = role ? String(role).toUpperCase() : null;

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
  }, [location]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (
    normalizedRole &&
    normalizedRole !== "CHANNEL_PARTNER" &&
    ROLE_BASED_DEFAULT_ROUTE[normalizedRole]
  ) {
    return <Navigate to={ROLE_BASED_DEFAULT_ROUTE[normalizedRole]} replace />;
  }

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.layout === "/channel-partner") {
        // Determine allowed roles for this route if specified on the route object.
        const routeRoles = Array.isArray(prop.roles)
          ? prop.roles.map((r) => String(r).toUpperCase())
          : null;

        const isDashboardRoute = prop.path === "/dashboard";

        const isAllowed =
          normalizedRole === "CHANNEL_PARTNER" ||
          isDashboardRoute ||
          (routeRoles && normalizedRole && routeRoles.includes(normalizedRole));

        if (isAllowed) {
          return <Route path={prop.path} element={prop.component} key={key} />;
        }

        // Not allowed -> redirect to dashboard
        //this path will be changed in future
        return (
          <Route
            path={prop.path}
            element={<Navigate to={ROLE_BASED_DEFAULT_ROUTE[normalizedRole]} replace />}
            key={key}
          />
        );
      }
      return null;
    });
  };

  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  const isRouteAllowedForSidebar = (prop) => {
    if (!prop) return false;
    if (prop.layout !== "/channel-partner") return false;
    const routeRoles = Array.isArray(prop.roles)
      ? prop.roles.map((r) => String(r).toUpperCase())
      : null;
    const isDashboard = prop.path === "/dashboard";
    return (
      normalizedRole === "CHANNEL_PARTNER" ||
      isDashboard ||
      (routeRoles && normalizedRole && routeRoles.includes(normalizedRole))
    );
  };

  const sidebarRoutes =
    normalizedRole === "CHANNEL_PARTNER"
      ? routes
      : // build a filtered routes array that preserves collapse groups only when children are allowed
      routes
        .map((prop) => {
          if (prop.collapse && Array.isArray(prop.views)) {
            const allowedViews = prop.views.filter(isRouteAllowedForSidebar);
            if (allowedViews.length) {
              return { ...prop, views: allowedViews, show: prop.show };
            }
            return null;
          }

          return isRouteAllowedForSidebar(prop) ? prop : null;
        })
        .filter(Boolean);

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
        return routes[i].name;
      }
    }
    return "Brand";
  };

  // toggles collapse between mini sidenav and normal
  const toggleSidenav = () => {
    const isMobile = window.innerWidth < 1200;

    if (isMobile) {
      const isOpen = document.body.classList.contains("g-sidenav-show");

      if (isOpen) {
        document.body.classList.remove("g-sidenav-show");
        document.body.classList.remove("g-sidenav-pinned");
        setSidenavOpen(false);
      } else {
        document.body.classList.add("g-sidenav-show");
        document.body.classList.add("g-sidenav-pinned");
        setSidenavOpen(true);
      }
    } else {
      if (document.body.classList.contains("g-sidenav-pinned")) {
        document.body.classList.remove("g-sidenav-pinned");
        document.body.classList.add("g-sidenav-hidden");
      } else {
        document.body.classList.add("g-sidenav-pinned");
        document.body.classList.remove("g-sidenav-hidden");
      }
      setSidenavOpen(!sidenavOpen);
    }
  };

  const getNavbarTheme = () => {
    return location.pathname.indexOf("admin/alternative-dashboard") === -1
      ? "dark"
      : "light";
  };

  return (
    <>
      <Sidebar
        routes={sidebarRoutes}
        toggleSidenav={toggleSidenav}
        sidenavOpen={sidenavOpen}
        logo={{
          innerLink: "/",
          imgSrc: require("assets/img/brand/argon-react.png"),
          imgAlt: "...",
        }}
      />
      <div className="main-content" ref={mainContentRef}>
        <AdminNavbar
          theme={getNavbarTheme()}
          toggleSidenav={toggleSidenav}
          sidenavOpen={sidenavOpen}
          brandText={getBrandText(location.pathname)}
        />
        <Routes>
          {getRoutes(routes)}
          <Route
            path="*"
            element={<Navigate to={ROLE_BASED_DEFAULT_ROUTE[normalizedRole]} replace />}
          />
        </Routes>
        {/* <AdminFooter /> */}
      </div>
      {sidenavOpen ? (
        <div className="backdrop d-xl-none" onClick={toggleSidenav} />
      ) : null}
    </>
  );
}

export default ChannelPartner;
